import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet-async';

import monitorMain from '../Images/multiparamonitor.jpeg';
import monitorAlt from '../Images/multilowend.jpeg';
import monitor601215 from '../Images/multi60.jpeg';
import image2 from '../Images/multihigh2.jpeg';
import heroImage from '../Images/multimonitorpage.jpeg';
import Footer from '../Pages/Footer';

const imageDetails = {
  [monitorMain]: {
    category: 'Multipara Monitor',
    heading: 'Multipara Monitor (High End)',
    title: 'Model: MT 70 Plus',
    subtitle: 'Advanced Multi-Parameter Patient Monitor',
    images: [monitorMain, image2],
    description:
      'The MT 70 Plus is an advanced multi-parameter patient monitor designed for accurate and continuous monitoring in operating rooms, ICU/CCU, emergency, and critical care environments.',

    sections: {
      'Clinical Applications': {
        icon: '🏥',
        items: [
          'Suitable for operating room, ICU/CCU ward, postoperative observation room and emergency rooms',
          'Supports adult, pediatric, and neonate monitoring',
        ],
      },

      'Monitoring Parameters': {
        icon: '📊',
        items: [
          'ECG with ST-segment measurement and arrhythmia analysis',
          'RESP, SpO2, PR, NIBP, TEMP, IBP, and CO2 monitoring',
          'Display, review, print, and storage of monitored information',
        ],
      },

      'Display & User Interface': {
        icon: '🖥️',
        items: [
          '13/15/17.3" high-definition touch screen',
          'Easy and convenient operation',
          'Elegant and concise appearance',
        ],
      },

      'Alarm & Safety Functions': {
        icon: '🔔',
        items: [
          'Independent physiological and technical alarm functions',
          'Luminous ON/OFF button for convenient night operation',
        ],
      },

      'Modular Design & Expansion': {
        icon: '🔧',
        items: [
          'Modular design for flexible expansion and upgrades',
          'Dynamic hot-plugging parameter modules',
          'Plug-and-play operation without restarting',
        ],
      },

      'Performance & Analysis': {
        icon: '📈',
        items: [
          'High-performance processor for stable and fast information processing',
          'Rapid patient information input',
          'Quick switching between adult, pediatric, and neonate modes',
        ],
      },

      'Advanced Monitoring Features': {
        icon: '⚡',
        items: [
          '23 arrhythmia analysis functions',
          'Full-lead ST-segment and pacing analysis',
          'Drug, hemodynamic, and renal function calculations',
          'Waveform freezing and holographic waveform storage & review',
        ],
      },

      'Connectivity & Data Management': {
        icon: '📡',
        items: [
          'Automatic data storage during power failure',
          'HDMI interface for additional display',
          'Optional 3-channel built-in recorder',
          'WiFi and wired Central Monitoring System connectivity',
        ],
      },

      'Power & Operation': {
        icon: '🔋',
        items: [
          'Built-in detachable lithium battery',
          'Fanless design for noiseless and low-power operation',
          'Standby mode for ICU workflow optimization and patient rest',
        ],
      },
    },
  },

  [monitorAlt]: {
    category: 'Multipara Monitor',
    heading: 'Multiparameter Monitor (Low End)',
    title: 'MT 30',
    subtitle: 'Compact Multi-Parameter Patient Monitor',
    images: [monitorAlt],

    description:
      'MT 30 is a compact and reliable multiparameter patient monitor designed for adult, pediatric, and neonatal monitoring. It provides real-time measurement of essential vital parameters with clear waveform display, alarm functions, trend storage, and central monitoring connectivity. Its portable design, rechargeable battery, and fanless operation make it suitable for hospitals, ICUs, wards, clinics, and patient transport.',

    sections: {
      'Advanced Monitoring': {
        icon: '📈',
        items: [
          'ECG waveform with HR, Arrhythmia, Pace & ST-segment analysis',
          'Respiration rate and respiration waveform display',
          'SpO₂ monitoring with anti-motion and anti-ambient light technology',
          'NIBP measurement with Manual, AUTO & STAT modes',
        ],
      },

      'Smart Display & Operation': {
        icon: '🖥️',
        items: [
          '7-lead ECG waveform display on one screen',
          'Standard interface, oxygen graph, trend graph and big character view',
          'Operated by keys and rotary knob',
          'Touchscreen operation available as optional',
        ],
      },

      'Data Storage & Review': {
        icon: '💾',
        items: [
          '96 hours graphical and tabular trend storage',
          'Review of 71 alarm events and 60 arrhythmia alarm events',
          'Storage for 4800-group NIBP data',
          'Waveform review and trend graph printing',
        ],
      },

      'Reliable Design': {
        icon: '🛡️',
        items: [
          'Fanless, quiet and energy-saving operation',
          'Reduces possibility of cross-infection',
          'Compact and portable body',
          'Lightweight design under 3 kg',
        ],
      },

      'Safety & Connectivity': {
        icon: '🔒',
        items: [
          'Connects to Central Monitoring System and Nurse Call System',
          'Supports 3G, Wi-Fi and wired network connection',
          'Defibrillation-proof design with special leads',
          'Anti-high-frequency surgical unit protection',
        ],
      },
    },
  },

  [monitor601215]: {
    category: 'Multipara Monitor',
    heading: 'Multipara Monitor (Pre-configured / Semi Modular)',
    title: 'MT 60',
    subtitle: 'Compact Multi-Parameter Patient Monitor',
    images: [monitor601215],

    description:
      'The MT 60 is a compact multi-parameter patient monitor designed for reliable real-time monitoring of adult, pediatric, and neonatal patients in various clinical environments.',

    sections: {
      'Clinical Applications': {
        icon: '🏥',
        items: [
          'Suitable for adult, pediatric, and neonatal monitoring',
          'Reliable patient monitoring for hospitals and clinics',
          'Supports continuous monitoring in various clinical environments',
        ],
      },

      'Display & User Interface': {
        icon: '🖥️',
        items: [
          '12/15" high-definition touch screen',
          'TFT/LCD display with clear vital sign visualization',
          'Real-time waveform and parameter display',
          'User-friendly operation interface',
          'Compact and portable monitor design',
        ],
      },

      'Monitoring Parameters': {
        icon: '📊',
        items: [
          'ECG monitoring',
          'SpO₂ monitoring',
          'NIBP monitoring',
          'Respiration monitoring',
          'Temperature monitoring',
          'Real-time patient parameter display',
        ],
      },

      'Alarm & Analysis Functions': {
        icon: '🔔',
        items: [
          'Audible and visual alarm functions',
          'High and low limit alarm settings',
          'Parameter trend review',
          'Continuous patient safety monitoring',
          'Reliable event notification support',
        ],
      },

      'Performance & Operation': {
        icon: '⚙️',
        items: [
          'Stable system operation',
          'Fast response and data acquisition',
          'Continuous monitoring capability',
          'Efficient workflow for healthcare staff',
          'Suitable for routine clinical use',
        ],
      },
    },
  },
};

const MultiparamonitorDetail = () => {
  const allImages = [monitorMain, monitorAlt, monitor601215];

  const [mainImage, setMainImage] = useState(monitorMain);
  const [relatedImages, setRelatedImages] = useState(
    allImages.filter((img) => img !== monitorMain)
  );
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useLayoutEffect(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleImageClick = (clickedImage) => {
    setMainImage(clickedImage);
    setRelatedImages(allImages.filter((img) => img !== clickedImage));
    setCurrentImageIndex(0);

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 50);
  };

  const detail = imageDetails[mainImage];
  const sections = detail?.sections || {};
  const productImages = detail?.images || [mainImage];
  const currentImageSrc = productImages[currentImageIndex];

  return (
    <>
      <Helmet>
        <title>Multipara Patient Monitor | Mindron Meditech</title>
        <meta
          name="description"
          content="Explore multipara patient monitors by Mindron Meditech including MT 70 Plus, MT 60, and MT 30 for ICU, emergency, operating room, wards, clinics, and patient monitoring applications."
        />
        <link
          rel="canonical"
          href="https://www.mindronmeditech.com/products/multiparamonitor"
        />
      </Helmet>

      <div
        style={{
          fontFamily: 'sans-serif',
          margin: 0,
          padding: 0,
          overflowX: 'hidden',
        }}
      >
        {/* HERO */}
        <div
          style={{
            height: isMobile ? '30vh' : '80vh',
            width: '100%',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <img
            src={heroImage}
            alt="Hero"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'top',
              display: 'block',
            }}
          />

          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(to top, rgba(0,20,60,0.5) 0%, transparent 60%)',
            }}
          />
        </div>

        {/* PRODUCT INTRO */}
        <div
          style={{
            display: 'flex',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: '40px',
            padding: isMobile ? '32px 6%' : '48px 6%',
            alignItems: 'flex-start',
          }}
        >
          {/* Left: text */}
          <div style={{ flex: 1, minWidth: '260px' }}>
            <div
              style={{
                display: 'inline-block',
                backgroundColor: '#e8f0fe',
                color: '#1a56db',
                fontSize: '13px',
                fontWeight: '600',
                padding: '4px 14px',
                borderRadius: '20px',
                marginBottom: '14px',
              }}
            >
              {detail?.category}
            </div>

            <h1
              style={{
                fontSize: isMobile ? '26px' : '38px',
                fontWeight: '800',
                color: '#0a1628',
                margin: '0 0 8px',
                lineHeight: 1.2,
              }}
            >
              {detail?.title}
            </h1>

            <h2
              style={{
                fontSize: isMobile ? '16px' : '20px',
                fontWeight: '500',
                color: '#444',
                margin: '0 0 20px',
              }}
            >
              {detail?.subtitle}
            </h2>

            <p
              style={{
                fontSize: '15px',
                color: '#555',
                lineHeight: '1.8',
                maxWidth: '520px',
              }}
            >
              {detail?.description}
            </p>
          </div>

          {/* Right: product image with arrows */}
          <div
            style={{
              flex: '0 0 auto',
              width: isMobile ? '100%' : '340px',
              background: '#f4f6fb',
              borderRadius: '14px',
              padding: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
            }}
          >
            {productImages.length > 1 && (
              <button
                onClick={() =>
                  setCurrentImageIndex(
                    (prev) =>
                      (prev - 1 + productImages.length) % productImages.length
                  )
                }
                style={{
                  fontSize: '18px',
                  padding: '6px 10px',
                  borderRadius: '6px',
                  border: '1px solid #ccc',
                  cursor: 'pointer',
                  background: '#fff',
                }}
              >
                ←
              </button>
            )}

            <img
              src={currentImageSrc}
              alt="Monitor"
              style={{
                flex: 1,
                maxHeight: '260px',
                objectFit: 'contain',
                borderRadius: '8px',
              }}
            />

            {productImages.length > 1 && (
              <button
                onClick={() =>
                  setCurrentImageIndex(
                    (prev) => (prev + 1) % productImages.length
                  )
                }
                style={{
                  fontSize: '18px',
                  padding: '6px 10px',
                  borderRadius: '6px',
                  border: '1px solid #ccc',
                  cursor: 'pointer',
                  background: '#fff',
                }}
              >
                →
              </button>
            )}
          </div>
        </div>

        {/* KEY FEATURES GRID */}
        <div
          style={{
            padding: isMobile ? '16px 6% 40px' : '16px 6% 56px',
          }}
        >
          <div
            style={{
              width: '50px',
              height: '5px',
              backgroundColor: '#cc0000',
              borderRadius: '2px',
              marginBottom: '12px',
            }}
          />

          <h2
            style={{
              fontSize: '22px',
              fontWeight: '800',
              color: '#0a1628',
              marginBottom: '28px',
            }}
          >
            Key Features
          </h2>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '20px',
            }}
          >
            {Object.entries(sections).map(([sectionName, sectionData], idx) => (
              <div
                key={idx}
                style={{
                  background: '#f4f6fb',
                  borderRadius: '12px',
                  padding: '22px 20px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '14px',
                  }}
                >
                  <span style={{ fontSize: '20px' }}>{sectionData.icon}</span>

                  <h3
                    style={{
                      fontSize: '15px',
                      fontWeight: '700',
                      color: '#0a1628',
                      margin: 0,
                    }}
                  >
                    {sectionName}
                  </h3>
                </div>

                <ul
                  style={{
                    margin: 0,
                    paddingLeft: '18px',
                    listStyle: 'disc',
                  }}
                >
                  {sectionData.items.map((item, i) => (
                    <li
                      key={i}
                      style={{
                        fontSize: '13px',
                        color: '#555',
                        lineHeight: '1.8',
                      }}
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* RELATED PRODUCTS */}
        <div
          style={{
            padding: isMobile ? '0 6% 48px' : '0 6% 60px',
          }}
        >
          <div
            style={{
              width: '50px',
              height: '5px',
              backgroundColor: '#cc0000',
              borderRadius: '2px',
              marginBottom: '12px',
            }}
          />

          <h2
            style={{
              fontSize: '22px',
              fontWeight: '800',
              color: '#0a1628',
              marginBottom: '24px',
            }}
          >
            Related Products
          </h2>

          <div
            style={{
              display: 'flex',
              gap: '20px',
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              paddingBottom: '4px',
            }}
          >
            {relatedImages.map((img, index) => (
              <div
                key={index}
                onClick={() => handleImageClick(img)}
                style={{
                  flex: '0 0 auto',
                  width: '220px',
                  background: '#f4f6fb',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow =
                    '0 8px 20px rgba(0,0,0,0.13)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow =
                    '0 2px 8px rgba(0,0,0,0.07)';
                }}
              >
                <div
                  style={{
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '150px',
                  }}
                >
                  <img
                    src={img}
                    alt={`Related ${index}`}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '120px',
                      objectFit: 'contain',
                    }}
                  />
                </div>

                <div
                  style={{
                    background: '#001f4d',
                    padding: '10px 14px',
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      fontSize: '13px',
                      color: '#fff',
                      fontWeight: '600',
                    }}
                  >
                    {imageDetails[img]?.title || `Product ${index + 1}`}
                  </p>

                  <p
                    style={{
                      margin: '4px 0 0',
                      fontSize: '11px',
                      color: '#aac4ff',
                    }}
                  >
                    {imageDetails[img]?.category}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Footer />

        <style>{`
          @media (max-width: 768px) {
            .header-container {
              height: 30vh !important;
            }
          }

          *::-webkit-scrollbar {
            display: none;
          }

          * {
            scrollbar-width: none;
          }

          body {
            overflow-x: hidden;
          }
        `}</style>
      </div>
    </>
  );
};

export default MultiparamonitorDetail;